// Client API minimaliste : wrap fetch + JWT.

const RAW_BASE = (import.meta.env.VITE_API_URL as string | undefined) || "/api";
export const API_BASE = RAW_BASE.replace(/\/$/, "");

const TOKEN_KEY = "eflux.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { json?: unknown } = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let body = init.body;
  if (init.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.json);
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, body });
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (data as { error?: string })?.error || res.statusText || "Erreur";
    if (res.status === 401) {
      setToken(null);
      // Ne pas rediriger ici : laisser l'AuthContext gérer.
    }
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export { ApiError };

/* --- DTOs --- */
export type Status = "draft" | "pending" | "review" | "validated" | "archived";
export type Role = "admin" | "user";
export type ExpenseStatus = "submitted" | "taken_into_account" | "rejected";

export interface User {
  id: string;
  identifier: string;
  display_name: string;
  email: string | null;
  role: Role;
  active: number;
  must_change_password: number;
}

export interface Entity {
  id: string;
  name: string;
  short_code: string;
  kind: string;
  color?: string | null;
  active?: number;
  deactivated_at?: number | null;
  purge_scheduled_at?: number | null;
}

export type MessageKind = "general" | "invoice" | "phone" | "voice";

export interface InvoicePayload {
  client: string;
  label: string;
  amount_ht_cents: number;
  has_fees: boolean;
  fees_cents?: number | null;
}
export interface PhonePayload {
  client_name: string;
  call_time: number;
  file: string;
  callback_number?: string | null;
  message: string;
}

export interface Counters {
  pending_by_entity: Record<string, number>;
  expenses_to_process: number;
  unread_messages: number;
}
export interface Document {
  id: string;
  entity_id: string;
  entity_code: string;
  entity_name?: string;
  title: string;
  description: string | null;
  status: Status;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: number;
  updated_at: number;
  validated_at: number | null;
  purged_at: number | null;
  created_by: string;
  created_by_name: string;
  assigned_to?: string | null;
  r2_key?: string | null;
}
export interface HistoryEntry {
  id: string;
  document_id: string;
  user_id: string;
  user_name: string;
  from_status: Status | null;
  to_status: Status;
  note: string | null;
  created_at: number;
}
export interface Annotation {
  id: string;
  document_id: string;
  user_id: string;
  user_name: string;
  identifier: string;
  body: string;
  page: number | null;
  resolved: number;
  created_at: number;
}
export interface ReplyRef {
  id: string;
  user_id: string | null;
  user_name: string | null;
  identifier: string | null;
  kind: MessageKind | null;
  body: string | null;
}

export interface Message {
  id: string;
  document_id?: string | null;
  user_id: string;
  user_name: string;
  identifier: string;
  recipient_user_id?: string | null;
  recipient_name?: string | null;
  recipient_identifier?: string | null;
  body: string;
  kind?: MessageKind;
  payload?: InvoicePayload | PhonePayload | null;
  read_at?: number | null;
  processed_at?: number | null;
  deliver_at?: number | null;
  delivered_at?: number | null;
  deleted_at?: number | null;
  audio_r2_key?: string | null;
  audio_duration_sec?: number | null;
  audio_transcript?: string | null;
  audio_summary?: string | null;
  reply_to_message_id?: string | null;
  reply_to?: ReplyRef | null;
  created_at: number;
}

export interface DirectoryUser {
  id: string;
  identifier: string;
  display_name: string;
  role: Role;
}
export interface Expense {
  id: string;
  entity_id: string;
  entity_code: string;
  entity_name?: string;
  user_id: string;
  user_name: string;
  user_identifier: string;
  expense_date: number;
  amount_cents: number;
  currency: string;
  category: string | null;
  label: string;
  notes: string | null;
  status: ExpenseStatus;
  r2_key: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  decision_note?: string | null;
  decided_at: number | null;
  decided_by?: string | null;
  decided_by_name?: string | null;
  taken_into_account_at?: number | null;
  created_at: number;
  updated_at: number;
}
